import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useToast } from "@/hooks/use-toast";

const MESSAGES: Record<string, { title: string; description: string }> = {
  saved: {
    title: "Preferences saved",
    description: "We'll only send the product emails you left on.",
  },
  unsubscribed: {
    title: "Unsubscribed from product emails",
    description:
      "Billing, renewal and cancellation notices are still sent while you have a subscription.",
  },
};

/**
 * Turns `?email_prefs=` into a toast.
 *
 * The preference page is served by the API on its own domain and has no toast of its own, so it
 * records the change, redirects here, and hands the outcome over in the query string. Someone
 * arriving from an email link is usually logged out, which is why the confirmation has to survive
 * the hop rather than living on the page that did the work.
 *
 * The flag is stripped before the toast fires: a refresh, a back-button, or a URL pasted to
 * somebody else must not replay a confirmation for something that did not just happen. An
 * unrecognised value is dropped the same way, silently — it confirms nothing.
 */
const EmailPrefsToast = () => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("email_prefs");
    if (!outcome) return;

    params.delete("email_prefs");
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true },
    );

    const message = MESSAGES[outcome];
    if (message) toast(message);
  }, [location.pathname, location.search, navigate, toast]);

  return null;
};

export default EmailPrefsToast;
